import { getBasicInfo } from '@wk/wk-gatherer';
import { useRouter } from 'next/router';
import React, {
  cloneElement,
  isValidElement,
  useContext,
  useState,
} from 'react';

import Button, { ButtonProps } from '@/components/Button';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';
import useIsMobile from '@/hooks/useIsMobile';
import useReserveInfo from '@/hooks/useReserveInfo';
import { CHINESE_COURSE_URL } from '@/utils/constants';
import { setTriggerEvent, setTriggerPvid } from '@/utils/token';
import { objToQuery } from '@/utils/url';
import { checkIfGoToGS20 } from '@/utils/utils';

import ReserveOtherSubject from '../IndependentAppointment/ReserveOtherSubject';
import ToStuCenter from '../IndependentAppointment/ToStuCenter';
import Modal from '../Modal';

interface IProps extends ButtonProps {
  subject?: 'chinese' | 'english' | 'math';
  isBlob?: boolean;
  /** 是否使用自定义按钮组件（如 wk-uni-lib Button），children 需要是可点击的按钮元素 */
  asCustomButton?: boolean;
  /** 退出约课流程后的回调地址，传入后约课页退出时会重定向到该地址 */
  callbackUrl?: string;
}

const LeadButton: React.FC<IProps> = ({
  children,
  asCustomButton = false,
  ...restProps
}) => {
  const { student } = useContext(GlobalLoginContext);
  const router = useRouter();
  const { isReserveAllSubject, isReserve } = useReserveInfo();
  const [stuModalVisible, setStuModalVisible] = useState<boolean>(false);
  const [otherSubjectModalVisible, setOtherSubjectModalVisible] =
    useState<boolean>(false);
  const isMobile = useIsMobile();
  const { subject } = restProps || {};

  const toReservePage = (
    reserveSubject?: 'chinese' | 'english' | 'math' | undefined,
  ) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const l = urlSearchParams.get('l') || '';
    const queryString = objToQuery({
      subject: reserveSubject || '',
      booking_triggerevent: restProps?.traceName || '',
      l,
      ...(restProps.callbackUrl ? { callbackUrl: restProps.callbackUrl } : {}),
    });
    if (checkIfGoToGS20(reserveSubject, student, queryString)) {
      return;
    }
    router.push(`/independent-appointment/${queryString}`);
  };

  const handleClick = () => {
    setTriggerPvid(getBasicInfo().pvid as string);
    setTriggerEvent(restProps?.traceName as string);

    if (restProps.isBlob) {
      window.open(CHINESE_COURSE_URL);
      return;
    }

    const rules = [
      /* 已登录，不带科目，没有约过所有科目 跳不带科目约课页 */
      {
        match: () => student && !subject && !isReserveAllSubject,
        action: () => toReservePage(),
      },
      /* 已登录，不带科目，约过所有科目 打开引导用户进入学员中心弹窗 */
      {
        match: () => student && !subject && isReserveAllSubject,
        action: () => setStuModalVisible(true),
      },
      /* 已登录，带科目，且当前科目未约过课，跳带科目约课页 */
      {
        match: () => student && subject && !isReserve(subject),
        action: () => toReservePage(subject),
      },
      /* 已登录，带科目，且当前科目约过课，其他科目未约过课，打开引导用户约其他科目弹窗 */
      {
        match: () =>
          student && subject && isReserve(subject) && !isReserveAllSubject,
        action: () => setOtherSubjectModalVisible(true),
      },
      /* 已登录，带科目，且当前科目约过课，其他科目都约了课，打开引导用户进入学员中心弹窗 */
      {
        match: () =>
          student && subject && isReserve(subject) && isReserveAllSubject,
        action: () => setStuModalVisible(true),
      },
      /* 未登录，带科目，跳带科目约课页 */
      {
        match: () => !student && subject,
        action: () => toReservePage(subject),
      },
      /* 未登录，不带科目，跳不带科目约课页 */
      {
        match: () => !student && !subject,
        action: () => toReservePage(),
      },
    ];

    const { action } = rules.find(({ match }) => match()) || {};
    action?.();
  };

  // 渲染按钮部分
  const renderButton = () => {
    // 如果使用自定义按钮组件（如 wk-uni-lib Button）
    if (asCustomButton && isValidElement(children)) {
      return cloneElement(children as React.ReactElement, {
        onClick: handleClick,
      });
    }
    // 默认使用内部 Button 组件
    return (
      <Button onClick={handleClick} {...restProps}>
        {children}
      </Button>
    );
  };

  return (
    <>
      {renderButton()}
      <Modal
        visible={stuModalVisible}
        handleClose={() => setStuModalVisible(false)}
        width={isMobile ? 'calc(100vw - 2.5rem)' : 452}>
        <ToStuCenter />
      </Modal>
      <Modal
        visible={otherSubjectModalVisible}
        handleClose={() => setOtherSubjectModalVisible(false)}
        width={isMobile ? 'calc(100vw - 2.5rem)' : 452}>
        <ReserveOtherSubject
          subject={subject as 'chinese' | 'math' | 'english'}
        />
      </Modal>
    </>
  );
};

export default LeadButton;
